import React from 'react'

import { Standfirst1, Body3, Heading4, Join, Space } from '@gousto-internal/citrus-react'
import classnames from 'classnames'
import { useSelector } from 'react-redux'

import { MenuRecipeImage, MenuRecipe } from '@library/menu-service/queries/menuApiMenuRecipe/types'

import { getNumPortions } from 'selectors/basket'
import { getFeaturedImage, getRangeImages } from 'utils/image'

import { getBrowserType } from '../../../../selectors/browser'
import { RecipeTag } from '../RecipeTag'
import { AttributeGrid } from './AttributeGrid'
import { DetailCarousel } from './Carousel'
import { DetailAddRecipe } from './DetailAddRecipe'
import { DetailAllergenIngredients } from './DetailAllergenIngredients'
import { DetailError } from './DetailError'
import { DetailImage } from './DetailImage'
import { DetailIngredients } from './DetailIngredients'
import { DetailPerPortion } from './DetailPerPortion'
import { RecipeDetailsPageOptions } from './Options/RecipeDetailsPageOptions'
import { RecipeRating } from './Rating'
import { Title } from './Title'
import { DetailViewType } from './types'

import css from './Detail.css'
import titleCss from './DetailTitle.css'

type DetailProps = {
  id: string
  title: string
  error: boolean
  loading: boolean
  recipe: MenuRecipe
  reload: () => void
  onClose: () => void
  position?: string
}

type RenderRecipeImageProps = {
  browserType: string
  images: MenuRecipeImage[]
  isBlueyRecipe: boolean
  isFineDineIn: boolean
  title: string
  view: DetailViewType
}

const LoadingSkeleton = ({ style }: { style?: React.CSSProperties }) => (
  <span className={css.loadingSkeleton} style={style} data-testid="LoadingSkeleton" />
)

const renderRecipeImage = ({
  browserType,
  images,
  isBlueyRecipe,
  isFineDineIn,
  title,
  view,
}: RenderRecipeImageProps) => {
  const featuredImage = getFeaturedImage(images, 'detail', browserType)

  if (!isBlueyRecipe && !isFineDineIn) {
    return <DetailImage media={featuredImage} title={title} view={view} />
  }

  const rangeImages = getRangeImages(images)

  return (
    <DetailCarousel
      isBlueyRecipe={isBlueyRecipe}
      images={rangeImages}
      fallbackImage={featuredImage}
    />
  )
}

export const Detail = ({
  id,
  title,
  error,
  loading,
  recipe,
  reload,
  onClose,
  position = '0',
}: DetailProps) => {
  const browserType = useSelector(getBrowserType)

  const numPortions = useSelector(getNumPortions)

  const {
    basics,
    description,
    equipment,
    food_brand: foodBrand,
    images,
    is_chef_prepared: isChefPrepared,
    merchandising_tags: merchandisingTags,
    rating,
    surcharge,
    options,
    id: recipeUuid,
  } = recipe

  const isBlueyRecipe = foodBrand?.slug === 'bluey'
  const isFineDineIn = foodBrand?.slug === 'fine-dine-in'
  const view = isBlueyRecipe || isFineDineIn ? 'fineDineInDetail' : 'detail'

  const titleClass = classnames(titleCss.containerLG, {
    [titleCss.detailHeading]: view === 'detail',
    [titleCss.fineDineInDetailHeading]: view === 'fineDineInDetail',
  })

  return (
    <div
      className={css.modalContainer}
      role="button"
      tabIndex={0}
      onKeyPress={onClose}
      onClick={onClose}
      data-testing="menuRecipeDetailsClose"
    >
      <div
        role="button"
        tabIndex={0}
        className={css.container}
        onKeyPress={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={css.header}>
          <div className={css.stickyHeader}>
            <div className={css.titleFlex}>
              <Title className={titleClass} title={title} />
              <span
                className={css.closeIcon}
                role="button"
                tabIndex={0}
                onKeyPress={onClose}
                onClick={onClose}
                data-testing="menuRecipeDetailsClose"
              />
            </div>
          </div>
          <div className={css.badges}>
            <div className={css.titleHidden} data-testing="recipeDetailTitle">
              <Title className={titleClass} title={title} />
            </div>
            <span className={css.recipeTagHolder}>
              <RecipeTag tag={merchandisingTags?.availability} />
            </span>
            <RecipeRating count={rating?.count} average={rating?.average} />
          </div>
        </div>
        <div className={css.imageContainer}>
          {renderRecipeImage({
            browserType,
            isBlueyRecipe,
            isFineDineIn,
            images,
            title,
            view,
          })}
        </div>
        <div className={css.sectionPanel}>
          <div className={css.variantsContainer}>
            <RecipeDetailsPageOptions recipeUuid={recipeUuid} options={options} />
          </div>
          <Join with={<Space size={4} />}>
            <Heading4>Recipe details</Heading4>
            <Standfirst1 data-testing="recipeDetailsDescription">
              {loading ? (
                <LoadingSkeleton style={{ height: '125px', width: '95%' }} />
              ) : (
                description
              )}
            </Standfirst1>
            <AttributeGrid recipe={recipe} loading={loading} />
            {loading && (
              <>
                <LoadingSkeleton style={{ height: '10px', width: '50%', marginBottom: '15px' }} />
                <LoadingSkeleton style={{ height: '10px', width: '55%' }} />
              </>
            )}
            {equipment && !!equipment.length && (
              <Body3>Equipment required: {equipment.map((eq) => eq.name).join(', ')} </Body3>
            )}
            {basics && !!basics.length && (
              <Body3>
                You&#8217;ll also need:{' '}
                {basics.map(({ name, slug }, idx) => (
                  <span key={slug}>
                    {name}
                    {basics.length - 1 !== idx && ', '}
                  </span>
                ))}{' '}
                <strong>(or dietary alternatives)</strong>
              </Body3>
            )}
          </Join>
        </div>
        {error ? (
          <DetailError reload={reload} />
        ) : (
          <>
            <div className={css.row} data-testing="recipeDetailsIngredients">
              <DetailIngredients loading={loading} recipe={recipe} />
            </div>
            <div className={css.row}>
              <DetailPerPortion loading={loading} recipe={recipe} numPortions={numPortions} />
            </div>
            <div className={css.row} data-testing="recipeDetailsAllergenIngredients">
              <DetailAllergenIngredients recipe={recipe} loading={loading} />
            </div>
          </>
        )}
        <div className={css.stickyContainer}>
          <DetailAddRecipe
            id={id}
            view={view}
            surcharge={surcharge?.price_per_portion}
            position={position}
            isChefPrepared={isChefPrepared}
          />
        </div>
      </div>
    </div>
  )
}
